import type { IssuedQuoteVersion } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { QuoteAmountBreakdown } from '@/entities/quote';
import { formatCalendarDate, formatDate, formatKrw } from '@/shared/lib';

export interface QuoteAmountSummaryProps {
  /** 최신 발행본 — 아직 발행 전이면 null. */
  readonly version: IssuedQuoteVersion | null;
}

const EYEBROW_CLASS =
  'text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase';

/**
 * 확정 견적 금액 — 상품 상세의 예상 합계처럼 오른쪽에 큰 합계를 두고, 그 아래 상품 금액·배송비 구성을 적는다(SUPER-86 spec §7).
 * 값은 모두 최신 발행본 사본에서 읽는다.
 * 발행 전이면 운영자가 검토 중이라는 안내만 남긴다.
 */
export const QuoteAmountSummary = ({ version }: QuoteAmountSummaryProps): ReactElement => {
  if (version === null) {
    return (
      <section aria-label="확정 견적" className="flex flex-col gap-2 bg-muted/60 p-5">
        <p aria-hidden className={EYEBROW_CLASS}>
          Quotation
        </p>
        <p className="text-sm font-semibold">아직 발행된 견적서가 없습니다.</p>
        <p className="text-sm text-muted-foreground">
          운영자가 요청 내용을 검토한 뒤 확정 견적서를 보내 드립니다.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="확정 견적" className="flex flex-col gap-4 border-t border-foreground pt-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <p aria-hidden className={EYEBROW_CLASS}>
            Quotation
          </p>
          <p className="text-sm font-semibold">확정 견적 합계</p>
          <p className="text-xs text-muted-foreground tabular-nums">
            {version.version}차 발행 · {formatDate(version.issuedAt)} · 부가세 포함
          </p>
        </div>
        <p className="text-[2rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
          {formatKrw(version.totalAmount)}
        </p>
      </div>
      <QuoteAmountBreakdown version={version} />
      <dl className="grid grid-cols-3 divide-x divide-foreground/10 border-y border-foreground/10 text-sm">
        <div className="flex flex-col gap-1 py-3 pr-3">
          <dt className="text-xs text-muted-foreground">공급가액</dt>
          <dd className="tabular-nums">{formatKrw(version.supplyAmount)}</dd>
        </div>
        <div className="flex flex-col gap-1 px-3 py-3">
          <dt className="text-xs text-muted-foreground">부가세</dt>
          <dd className="tabular-nums">{formatKrw(version.vatAmount)}</dd>
        </div>
        <div className="flex flex-col gap-1 py-3 pl-3">
          <dt className="text-xs text-muted-foreground">유효기한</dt>
          <dd className="tabular-nums">{formatCalendarDate(version.validUntil)}</dd>
        </div>
      </dl>
    </section>
  );
};
